import {AfterViewChecked, Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Store} from "@ngrx/store";
import {MatDialog} from "@angular/material/dialog";
import {
    selectDealFundsOverview,
    selectIsAnyDealFormInEditMode,
    selectIsExchangeRateMissingOnFund,
    selectSelectedDealCurrencyIsoCode,
    selectSelectedDealId
} from "../../store/deal/deal.selectors";
import {CodeTableEnum} from "../../../shared/model/code";
import {DealFundsManageDialogComponent} from "../deal-funds-manage-dialog/deal-funds-manage-dialog.component";
import {DealFxRatesDialogComponent} from "../deal-fx-rates-dialog/deal-fx-rates-dialog.component";
import {combineLatest, filter, map, Subscription, switchMap, take} from "rxjs";
import {FxActions} from "../../../shared/store/shared.actions";
import {selectFxAutofillRate, selectFxLoadingMode} from "../../../shared/store/fx/fx.selectors";
import {FxAutofillSlice, fxAutofillSource, isFxRateEmpty} from "../../../shared/store/fx/fx-autofill";
import {DealFundActions} from "../../store/deal.actions";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {SellerPosition} from "../../models/seller-position";
import {DealFundRecord} from "../../models/deal-fund-record";
import {numberNotBiggerThan190000m} from "../../../shared/utils/form-validators";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {TraceableCode, TraceableDate, TraceableMoney, TraceableMultiple, TraceablePercent, TraceableText} from "../../../shared/model/traceable";
import {TableSettingsDialogComponent, TableSettingsDialogData, TableSettingsDialogResult} from "../../../shared/user-setting/table-settings-dialog/table-settings-dialog.component";
import {TableSort} from "../../../shared/generated-sources/api/user-setting";
import {selectSettingsLoaded, selectTableColumns, selectTablePageSize, selectTableSort} from "../../../shared/user-setting/store/user-setting.selectors";
import {UserSettingActions} from "../../../shared/user-setting/store/user-setting.actions";

@Component({
    selector: "valumize-deal-funds",
    templateUrl: "./deal-funds.component.html",
    styleUrls: ["./deal-funds.component.scss"],
    standalone: false
})
export class DealFundsComponent implements OnInit, OnDestroy, AfterViewChecked {
    private static readonly TABLE_KEY = "deal-funds";
    private static readonly ALL_COLUMNS: string[] = [
        // Fund Details
        "fundValuationDescription", "fundSize", "fundCurrency", "exchangeRate",
        "fundSizeInDealCurrency", "fundVintageYear", "fundEndYear", "fundStage",
        // Fund Report (Seller)
        "reportDate", "sellerStake", "sellerCommitment", "sellerCommitmentDealCurrency",
        "nTvpi", "breakevenMoic", "navWeightedGpEvEbitda",
        // Forecast (Buyer)
        "closingDate", "buyerStake", "buyerCommitment", "discountToNav",
        "bidPriceBuyerStake", "navBuyerStake", "unfundedBuyerStake",
        "exposureBuyerStake", "adjustedCommitment"
    ];
    private static readonly DEFAULT_HIDDEN: ReadonlySet<string> = new Set([
        "fundSizeInDealCurrency", "breakevenMoic", "exposureBuyerStake"
    ]);
    private static readonly DEFAULT_COLUMNS: string[] =
        DealFundsComponent.ALL_COLUMNS.filter(c => !DealFundsComponent.DEFAULT_HIDDEN.has(c));

    private static readonly COL_GROUP: Record<string, string> = {
        fundValuationDescription: "Fund",
        fundSize: "Fund",
        fundCurrency: "Fund",
        exchangeRate: "Fund",
        fundSizeInDealCurrency: "Fund",
        fundVintageYear: "Fund",
        fundEndYear: "Fund",
        fundStage: "Fund",

        reportDate: "Fund Report (Seller)",
        sellerStake: "Fund Report (Seller)",
        sellerCommitment: "Fund Report (Seller)",
        sellerCommitmentDealCurrency: "Fund Report (Seller)",
        nTvpi: "Fund Report (Seller)",
        breakevenMoic: "Fund Report (Seller)",
        navWeightedGpEvEbitda: "Fund Report (Seller)",

        closingDate: "Forecast (Buyer)",
        buyerStake: "Forecast (Buyer)",
        buyerCommitment: "Forecast (Buyer)",
        discountToNav: "Forecast (Buyer)",
        bidPriceBuyerStake: "Forecast (Buyer)",
        navBuyerStake: "Forecast (Buyer)",
        unfundedBuyerStake: "Forecast (Buyer)",
        exposureBuyerStake: "Forecast (Buyer)",
        adjustedCommitment: "Forecast (Buyer)"
    };

    readonly COL_LABEL: Record<string, string> = {
        fundValuationDescription: "Description",
        fundSize: "Fund Size*",
        fundCurrency: "Fund Currency",
        exchangeRate: "Exchange Rate",
        fundSizeInDealCurrency: "Fund Size",
        fundVintageYear: "Fund Vintage Year",
        fundStage: "Fund Stage",
        fundEndYear: "Fund End Year",
        reportDate: "Report Date",
        closingDate: "Closing Date",
        sellerStake: "Seller Stake",
        sellerCommitment: "Seller Commitment*",
        sellerCommitmentDealCurrency: "Seller Commitment",
        buyerStake: "Buyer Stake",
        buyerCommitment: "Buyer Commitment",
        nTvpi: "Current nTVPI",
        breakevenMoic: "Breakeven MOIC",
        discountToNav: "Discount to NAV",
        bidPriceBuyerStake: "Bid Price (Buyer Stake)",
        navBuyerStake: "NAV (Buyer Stake)",
        unfundedBuyerStake: "Unfunded (Buyer Stake)",
        adjustedCommitment: "Secondary Commitment",
        exposureBuyerStake: "Exposure (Buyer Stake)",
        navWeightedGpEvEbitda: "NAV Weighted EV/EBITDA"
    };

    private readonly store = inject(Store);
    private readonly destroyRef = inject(DestroyRef);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);
    private readonly subscriptions: Subscription[] = [];

    private settingsApplied = false;
    private defaultApplied = false;
    savedPageSize = 20;
    private savedSort?: TableSort;
    selectedColumns: string[] = [...DealFundsComponent.DEFAULT_COLUMNS];

    dealId$ = this.store.select(selectSelectedDealId);
    dealFunds$ = this.store.select(selectDealFundsOverview);
    isEditDisabled$ = this.store.select(selectIsAnyDealFormInEditMode);
    isExchangeRateMissing$ = this.store.select(selectIsExchangeRateMissingOnFund);
    selectedDealCurrency$ = this.store.select(selectSelectedDealCurrencyIsoCode);

    isEditable = false;
    codeTableCurrency = CodeTableEnum.SHARED_CURRENCY;
    codeTableStage = CodeTableEnum.SHARED_STAGES;
    persistedSellerPositions: SellerPosition[] = [];

    // Inline FX autofill (US-1171), keyed by fundName.text
    fxAsOfDate: Record<string, string | null> = {};
    autofilledUntouched: Record<string, boolean> = {};
    fxEditedDate: Record<string, Date | null> = {};
    private fxForceOverwrite: Record<string, boolean> = {};
    private previousIsEditable = false;
    private readonly fxValueChangesRegistered = new Set<string>();
    // Row names with an autofill result subscription still pending. Guards against a rapid second
    // refresh-click dispatching a duplicate ECB request and stacking another result subscription
    // for the same row (US-1171); cleared when the result (LOADED/ERROR) arrives.
    private readonly fxRowInFlight = new Set<string>();
    // Read as a take(1) snapshot wherever autofill runs (US-1171) — the deal currency can change
    // during the component's life, so this stays an observable rather than a captured value.
    private readonly dealCurrency$ = this.store.select(selectSelectedDealCurrencyIsoCode);

    dealFundsDataSource: MatTableDataSource<DealFundRecord> = new MatTableDataSource<DealFundRecord>();
    dealFundsColumns: string[] = [];
    groupBands: { id: string; label: string; colspan: number; isStart: boolean }[] = [];
    groupBandIds: string[] = [];
    readonly groupStartCols = new Set<string>();

    isGroupStart(col: string): boolean { return this.groupStartCols.has(col); }

    @ViewChild("dealFundsSort") dealFundsSort!: MatSort;
    @ViewChild(MatPaginator) paginator?: MatPaginator;
    isSortApplied = false;

    constructor() {
    }

    dealFundsForm: FormGroup = this.formBuilder.group({
        bidPriceAdjustment: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]})
    });

    ngOnInit() {
        this.loadSavedSettings();
        this.subscriptions.push(this.dealFunds$.pipe(map((dealFund) => {
            this.isEditable = dealFund.dealFundsIsEditable;
            this.persistedSellerPositions = dealFund.sellerPositions;

            const bidPriceAdjustmentRow = dealFund.tableDatasource.find(row => row.fundName.text === "Bid Price Adjustment");
            this.dealFundsForm.controls["bidPriceAdjustment"].patchValue(
                DecimalFormatPipe.transformFromMillionsNum(bidPriceAdjustmentRow?.bidPriceBuyerStake?.amount ?? undefined)
            );

            dealFund.tableDatasource
                .filter(dealFundRecord => dealFundRecord.fundName.text != null && !["Bid Price Adjustment", "Total"].includes(dealFundRecord.fundName.text))
                .forEach(dealFundRecord => {
                    this.dealFundsForm.addControl(
                        dealFundRecord.fundName.text + "BuyerStake",
                        this.formBuilder.control<number | null>(null, {validators: numberNotBiggerThan190000m()})
                    );
                    this.dealFundsForm.addControl(
                        dealFundRecord.fundName.text + "ExchangeRate",
                        this.formBuilder.control<number>(1, {nonNullable: true, validators: [Validators.required, numberNotBiggerThan190000m(), Validators.min(0)]})
                    );
                    this.dealFundsForm.controls[dealFundRecord.fundName.text + "BuyerStake"]?.patchValue(
                        DecimalFormatPipe.transformFractionToPercent(dealFundRecord.sellerPosition.buyerStake.fraction ?? undefined)
                    );
                    // emitEvent:false so this programmatic (re)load of the persisted rate — which also
                    // runs on the post-save re-emit — does not fire the valueChanges handler below and
                    // mis-flag an untouched autofilled rate as "user edited" (US-1171).
                    this.dealFundsForm.controls[dealFundRecord.fundName.text + "ExchangeRate"]?.patchValue(dealFundRecord.exchangeRate.fraction, {emitEvent: false});
                    this.registerFxValueChanges(dealFundRecord.fundName.text);
                });

            // FX autofill (US-1171): fire once when the table transitions into edit mode, after the
            // per-row ExchangeRate controls above exist (patching a missing control is a no-op).
            const enteredEdit = !this.previousIsEditable && dealFund.dealFundsIsEditable;
            const exitedEdit = this.previousIsEditable && !dealFund.dealFundsIsEditable;
            this.previousIsEditable = dealFund.dealFundsIsEditable;
            if (enteredEdit) {
                this.runDealFundsAutofill(dealFund.tableDatasource);
            } else if (exitedEdit) {
                // Cancel/save returns to read-only: discard the in-progress autofill provenance so a
                // canceled "as of …" suggestion does not linger into the next edit session (US-1171).
                this.resetFxAutofillState();
            }

            this.dealFundsDataSource.data = dealFund.tableDatasource;
            this.applySelectedColumns();

            this.dealFundsDataSource.sortingDataAccessor = (row: DealFundRecord, columnName: string): string | number => {
                if (["fundName", "fundValuationDescription"].includes(columnName)) {
                    return (row[columnName as keyof DealFundRecord] as TraceableText).text ?? "";
                } else if (["fundCurrency", "fundStage"].includes(columnName)) {
                    return (row[columnName as keyof DealFundRecord] as TraceableCode).code ?? "";
                } else if (["fundVintageYear", "fundEndYear", "reportDate", "closingDate"].includes(columnName)) {
                    return (row[columnName as keyof DealFundRecord] as TraceableDate).date ?? "";
                } else if (["fundSize", "fundSizeInDealCurrency", "sellerCommitmentDealCurrency", "bidPriceBuyerStake", "navBuyerStake",
                    "unfundedBuyerStake", "adjustedCommitment", "gpCarryReserveBuyerStake", "exposureBuyerStake"].includes(columnName)) {
                    return (row[columnName as keyof DealFundRecord] as TraceableMoney).amount ?? 0;
                } else if (["nTvpi", "breakevenMoic", "navWeightedGpEvEbitda"].includes(columnName)) {
                    return (row[columnName as keyof DealFundRecord] as TraceableMultiple).factor ?? 0;
                } else if (["exchangeRate", "discountToNav"].includes(columnName)) {
                    return (row[columnName as keyof DealFundRecord] as TraceablePercent).fraction ?? 0;
                } else if (columnName === "sellerStake") {
                    return row.sellerPosition?.sellerStake?.fraction ?? 0;
                } else if (columnName === "buyerStake") {
                    return row.sellerPosition?.buyerStake?.fraction ?? 0;
                } else if (columnName === "sellerCommitment") {
                    return row.sellerPosition?.sellerCommitment?.amount ?? 0;
                } else if (columnName === "buyerCommitment") {
                    return row.sellerPosition?.buyerCommitment?.amount ?? 0;
                }
                // fallback
                const value = (row as any)[columnName];
                return typeof value === "number" ? value : value ?? "";
            };

            const pinnedFundNames = ["Bid Price Adjustment", "Total"];

            // Custom sortData necessary to keep bid price adjustment and totals row at the bottom
            this.dealFundsDataSource.sortData = (data: DealFundRecord[], sort: MatSort) => {
                if (!sort.active || sort.direction === "") {
                    return data;
                }

                const isPinned = (row: DealFundRecord) => pinnedFundNames.includes(row.fundName?.text ?? "");

                const pinnedRows = data.filter(isPinned);
                const normalRows = data.filter(row => !isPinned(row));

                // normal Angular Material sorting logic, but only on normalRows
                normalRows.sort((a, b) => {
                    const valueA = this.dealFundsDataSource.sortingDataAccessor(a, sort.active);
                    const valueB = this.dealFundsDataSource.sortingDataAccessor(b, sort.active);

                    let comparator = 0;
                    if (valueA != null && valueB != null) {
                        if (typeof valueA === "number" && typeof valueB === "number") {
                            comparator = valueA - valueB;
                        } else {
                            comparator = ("" + valueA).localeCompare("" + valueB);
                        }
                    } else if (valueA != null) {
                        comparator = 1;
                    } else if (valueB != null) {
                        comparator = -1;
                    }

                    return sort.direction === "asc" ? comparator : -comparator;
                });

                // keep Bid Price Adjustment above Total
                pinnedRows.sort((a, b) => pinnedFundNames.indexOf(a.fundName?.text ?? "") - pinnedFundNames.indexOf(b.fundName?.text ?? ""));

                return [...normalRows, ...pinnedRows];
            };

            if (!this.defaultApplied && this.dealFundsSort?.sortables.size) {
                const initial: TableSort = this.savedSort ?? {field: "fundName", direction: "asc"};
                if (this.dealFundsSort.sortables.has(initial.field)) {
                    this.dealFundsSort.sort({id: initial.field, start: initial.direction, disableClear: true});
                    this.defaultApplied = true;
                }
            }
        })).subscribe());
    }

    private registerFxValueChanges(name: string | undefined): void {
        if (name == null || this.fxValueChangesRegistered.has(name)) {
            return;
        }
        const control = this.dealFundsForm.controls[name + "ExchangeRate"];
        if (!control) {
            return;
        }
        this.fxValueChangesRegistered.add(name);
        // Once the user edits an autofilled rate, drop the "untouched" provenance (save as "user").
        this.subscriptions.push(control.valueChanges.subscribe(() => {
            if (!this.fxForceOverwrite[name]) {
                this.autofilledUntouched[name] = false;
                this.fxEditedDate[name] = new Date();
            }
        }));
    }

    private resetFxAutofillState(): void {
        // fxValueChangesRegistered is intentionally not cleared: its valueChanges subscriptions live
        // for the component's lifetime, so re-registering would stack duplicate listeners.
        this.fxAsOfDate = {};
        this.autofilledUntouched = {};
        this.fxEditedDate = {};
        this.fxForceOverwrite = {};
        this.fxRowInFlight.clear();
    }

    private runDealFundsAutofill(records: DealFundRecord[]): void {
        this.store.select(selectSettingsLoaded).pipe(
            filter(Boolean),
            take(1),
            switchMap(() => combineLatest([
                this.store.select(selectFxLoadingMode),
                this.dealCurrency$
            ]).pipe(take(1))),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(([mode, dealCcy]) => {
            if (mode !== "AUTOMATIC") {
                return;
            }
            const dispatched = new Set<string>();
            records
                .filter(record => record.fundName.text != null && !["Bid Price Adjustment", "Total"].includes(record.fundName.text))
                .forEach(record => this.autofillRow(record, dealCcy, dispatched));
        });
    }

    private autofillRow(record: DealFundRecord, dealCcy: string | undefined, dispatched: Set<string>): void {
        const name = record.fundName.text;
        if (name == null) {
            return;
        }
        const control = this.dealFundsForm.controls[name + "ExchangeRate"];
        if (!control) {
            return;
        }
        // Judge emptiness from the persisted rate, not control.value: the control defaults to 1,
        // so a legitimately saved rate of exactly 1.0 (e.g. pegged currencies) must not look empty.
        const isEmpty = isFxRateEmpty(record.exchangeRate.fraction);
        if (!isEmpty && !this.fxForceOverwrite[name]) {
            return;
        }
        const fundCcy = record.fundCurrency.code;
        if (!fundCcy || !dealCcy) {
            return;
        }
        if (fundCcy === dealCcy) {
            control.patchValue(1);
            this.autofilledUntouched[name] = true;
            this.fxAsOfDate[name] = null;
            this.fxForceOverwrite[name] = false;
            return;
        }
        // mergeMap does not dedupe in-flight requests, so guard one dispatch per pair within this run.
        const key = `${fundCcy}_${dealCcy}`;
        if (!dispatched.has(key)) {
            this.store.dispatch(FxActions.fetchAutofillRate({from: fundCcy, to: dealCcy}));
            dispatched.add(key);
        }
        this.subscribeRowResult(name, fundCcy, dealCcy);
    }

    private subscribeRowResult(name: string, fundCcy: string, dealCcy: string): void {
        // A result subscription for this row is already pending — don't stack a second one.
        if (this.fxRowInFlight.has(name)) {
            return;
        }
        this.fxRowInFlight.add(name);
        this.store.select(selectFxAutofillRate(fundCcy, dealCcy)).pipe(
            filter((slice): slice is FxAutofillSlice => slice?.status === "LOADED" || slice?.status === "ERROR"),
            take(1),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((slice) => {
            this.fxRowInFlight.delete(name);
            const control = this.dealFundsForm.controls[name + "ExchangeRate"];
            if (!control) {
                return;
            }
            // In-flight guard: only write the fetched rate if the user hasn't typed since we
            // dispatched. The control still holding its default (1) or null means untouched.
            const stillUntouched = control.value == null || control.value === 1;
            if (slice.status === "LOADED" && slice.data.fraction != null && (stillUntouched || this.fxForceOverwrite[name])) {
                control.patchValue(slice.data.fraction);
                this.autofilledUntouched[name] = true;
                this.fxAsOfDate[name] = slice.data.asOfDate;
                this.fxForceOverwrite[name] = false;
            }
            // ERROR / no fraction (unsupported pair): leave the row → bg-exchange-rate-warning stays.
        });
    }

    refreshRow = (name: string) => {
        this.fxForceOverwrite[name] = true;
        this.dealCurrency$.pipe(take(1)).subscribe((dealCcy) => {
            const record = this.dealFundsDataSource.data.find(r => r.fundName.text === name);
            const fundCcy = record?.fundCurrency.code;
            if (!fundCcy || !dealCcy) {
                return;
            }
            if (fundCcy === dealCcy) {
                this.dealFundsForm.controls[name + "ExchangeRate"]?.patchValue(1);
                this.autofilledUntouched[name] = true;
                this.fxAsOfDate[name] = null;
                this.fxForceOverwrite[name] = false;
                return;
            }
            // Ignore the click if a fetch for this row is already in flight (e.g. rapid double-click,
            // or a refresh while the initial bulk autofill for this pair is still pending).
            if (this.fxRowInFlight.has(name)) {
                return;
            }
            this.store.dispatch(FxActions.fetchAutofillRate({from: fundCcy, to: dealCcy}));
            this.subscribeRowResult(name, fundCcy, dealCcy);
        });
    };

    private applySelectedColumns() {
        this.dealFundsColumns = ["fundName", ...this.selectedColumns];

        // Build the band row as one cell per contiguous run of same-group columns
        // (with a label-less placeholder for the sticky fundName). Each band cell
        // carries its own colspan so the band row aligns with the column-header
        // row beneath it (sum of colspans === dealFundsColumns.length).
        this.groupBands = [];
        this.groupStartCols.clear();
        const cg = DealFundsComponent.COL_GROUP;
        let prevGroup: string | null = "__INIT__" as unknown as string;
        for (const col of this.dealFundsColumns) {
            const g = cg[col] ?? null;
            if (g !== prevGroup) {
                this.groupBands.push({
                    id: `band-${this.groupBands.length}`,
                    label: g ?? "",
                    colspan: 1,
                    isStart: g != null,
                });
                prevGroup = g;
                if (g != null) this.groupStartCols.add(col);
            } else {
                this.groupBands[this.groupBands.length - 1].colspan++;
            }
        }
        this.groupBandIds = this.groupBands.map(b => b.id);
    }

    private loadSavedSettings() {
        if (this.settingsApplied) return;
        this.settingsApplied = true;
        this.subscriptions.push(
            this.store.select(selectSettingsLoaded).pipe(filter(Boolean), take(1)).subscribe(() => {
                this.store.select(selectTableColumns(DealFundsComponent.TABLE_KEY)).pipe(take(1)).subscribe(saved => {
                    const valid = new Set(DealFundsComponent.ALL_COLUMNS);
                    const filtered = saved?.filter(c => valid.has(c)) ?? [];
                    this.selectedColumns = filtered.length > 0 ? filtered : [...DealFundsComponent.DEFAULT_COLUMNS];
                    this.applySelectedColumns();
                });
                this.store.select(selectTablePageSize(DealFundsComponent.TABLE_KEY)).pipe(take(1)).subscribe(size => {
                    if (size) this.savedPageSize = size;
                });
                this.store.select(selectTableSort(DealFundsComponent.TABLE_KEY)).pipe(take(1)).subscribe(sort => {
                    if (sort) this.savedSort = sort;
                });
            })
        );
    }

    openTableSettings() {
        const s = this.dealFundsSort;
        const currentSort: TableSort = s?.active
            ? {field: s.active, direction: s.direction === "desc" ? "desc" : "asc"}
            : (this.savedSort ?? {field: "fundName", direction: "asc"});

        const colsForGroup = (label: string) =>
            DealFundsComponent.ALL_COLUMNS.filter(c => DealFundsComponent.COL_GROUP[c] === label);

        const dialogData: TableSettingsDialogData = {
            tableKey: DealFundsComponent.TABLE_KEY,
            columnGroups: [
                {label: "Fund",                cols: colsForGroup("Fund")},
                {label: "Fund Report (Seller)", cols: colsForGroup("Fund Report (Seller)")},
                {label: "Forecast (Buyer)",     cols: colsForGroup("Forecast (Buyer)")},
            ],
            columnLabels: this.COL_LABEL,
            currentColumns: this.selectedColumns,
            currentPageSize: this.savedPageSize,
            currentSort,
            pageSizeOptions: [10, 20, 30, 50, 100],
            sortableColumns: [
                {value: "fundName", label: "Fund Name"},
                {value: "fundSize", label: "Fund Size"},
                {value: "fundSizeInDealCurrency", label: "Fund Size"},
                {value: "exchangeRate", label: "Exchange Rate"},
                {value: "reportDate", label: "Report Date"},
                {value: "bidPriceBuyerStake", label: "Bid Price (Buyer Stake)"},
                {value: "navBuyerStake", label: "NAV (Buyer Stake)"},
                {value: "discountToNav", label: "Discount to NAV"},
            ],
            isAdmin: false,
            defaultColumns: [...DealFundsComponent.DEFAULT_COLUMNS],
            defaultPageSize: 20,
            defaultSort: {field: "fundName", direction: "asc"}
        };

        this.dialog.open(TableSettingsDialogComponent, {data: dialogData, width: "520px"})
            .afterClosed()
            .subscribe((result: TableSettingsDialogResult) => this.handleDialogResult(result));
    }

    private handleDialogResult(result: TableSettingsDialogResult) {
        if (!result) return;
        const tableKey = DealFundsComponent.TABLE_KEY;
        this.selectedColumns = result.columns;
        this.applySelectedColumns();
        this.savedPageSize = result.pageSize;
        this.savedSort = result.sort;
        if (this.paginator) this.paginator.pageSize = result.pageSize;
        if (this.dealFundsSort?.sortables.has(result.sort.field)) {
            this.dealFundsSort.sort({id: result.sort.field, start: result.sort.direction as "asc" | "desc", disableClear: true});
        }

        const payload = {tableKey, columns: result.columns, pageSize: result.pageSize, sort: result.sort};
        if (result.saveAdmin) this.store.dispatch(UserSettingActions.saveAdminTableSettings(payload));
        if (result.saveUser) this.store.dispatch(UserSettingActions.saveTableSettings(payload));
        if (!result.saveUser && !result.saveAdmin) this.store.dispatch(UserSettingActions.applyTableSettings(payload));
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    ngAfterViewChecked(): void {
        if (!this.isSortApplied && this.dealFundsSort) {
            this.dealFundsDataSource.sort = this.dealFundsSort;
            this.isSortApplied = true;
        }
        if (this.paginator && this.dealFundsDataSource.paginator !== this.paginator) {
            this.dealFundsDataSource.paginator = this.paginator;
        }
    }

    openManageFundsDialog(dealId: number) {
        const dialogRef = this.dialog.open(DealFundsManageDialogComponent);
        dialogRef.componentInstance.dealId = dealId;
    }

    openFxRatesDialog(dealId: number) {
        this.dialog.open(DealFxRatesDialogComponent, {
            data: {dealId},
            panelClass: "fx-rates-dialog-panel",
            width: "920px",
            maxHeight: "85vh"
        });
    }

    isEditableBidColumnCell(name: string): boolean {
        const editableRows = ["Bid Price Adjustment"];
        return editableRows.includes(name) && this.isEditable;
    }

    save(dealId: number, dealFundRecords: DealFundRecord[]) {
        if (this.dealFundsForm?.valid) {
            const form = this.dealFundsForm;
            const updatedSellerPositions: SellerPosition[] = [];

            const bidPriceAdjustmentValue = form.controls["bidPriceAdjustment"].value;
            const bidPriceAdjustment = DecimalFormatPipe.transformToMillionsNum(bidPriceAdjustmentValue);

            this.persistedSellerPositions.forEach(sp => {
                const dealFundRecord = dealFundRecords.find(df => df.fundValuationId === sp.fundValuationId);
                const name = dealFundRecord?.fundName.text ?? "";
                const buyerStake = form.controls[name + "BuyerStake"]?.value;
                const exchangeRate = form.controls[name + "ExchangeRate"]?.value;

                // Provenance (US-1171): untouched autofill persists its ECB as-of date; a user-edited
                // rate persists "user"; an unchanged rate keeps its existing source.
                const asOf = this.fxAsOfDate[name];
                const fxRateDealSource = fxAutofillSource(
                    this.autofilledUntouched[name],
                    asOf,
                    exchangeRate !== sp.fxRateDeal.fraction,
                    sp.fxRateDeal.source
                );

                const updatedSellerPosition = {
                    ...sp,
                    buyerStake: {
                        ...sp.buyerStake,
                        fraction: DecimalFormatPipe.transformPercentToFraction(buyerStake)
                    },
                    fxRateDeal: {
                        ...sp.fxRateDeal,
                        fraction: exchangeRate,
                        source: fxRateDealSource
                    }
                };
                updatedSellerPositions.push(updatedSellerPosition);
            });

            this.store.dispatch(DealFundActions.save({dealId, sellerPositions: updatedSellerPositions, bidPriceAdjustment}));
        }
    }

    editMode = () => this.store.dispatch(DealFundActions.edit());

    cancel = () => this.store.dispatch(DealFundActions.cancel());

    notClickableInEditMode(): string {
        return this.isEditable ? "not-clickable" : "";
    }

    noValueCell(row: any, hide: "allFooter" | "bidPrAdj" | "total"): boolean {
        if (hide === "allFooter") {
            const rowTypes = ["Bid Price Adjustment", "Total"];
            return (rowTypes.includes(row.fundName.text));
        } else if (hide === "bidPrAdj") {
            return (row.fundName.text === "Bid Price Adjustment");
        }
        return false;
    }

    styleRowImportant(row: any): string {
        const importantRowTypes = ["Total"];
        return (importantRowTypes.includes(row.fundName.text)) ? "row-important" : "";
    }

    styleRowNotClickable(row: any): string {
        const notClickableRowTypes = ["Bid Price Adjustment", "Total"];
        return (notClickableRowTypes.includes(row.fundName.text)) ? "not-clickable" : "";
    }

    styleRowBorderTop(row: any): string {
        const borderTopRowTypes = ["Bid Price Adjustment"];
        return (borderTopRowTypes.includes(row.fundName.text)) ? "row-top-border" : "";
    }
}
